import type { DataSource, SiteRagReport } from '../../types/siteRag';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { collectClaimMatchText, normaliseClaimTextForMatch } from '../renumberReferences';
import { hashCanonicalJson, sha256Hex } from '../reportGeneration/reportContractHash';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import {
  EXTRACT_KNOWN_ARRAY_SECTIONS,
  EXTRACT_KNOWN_OBJECT_SECTIONS,
  EXTRACT_KNOWN_STRING_SECTIONS,
} from './reportTextExtract';
import { SOURCE_ROLE_REVIEW_ROOTS } from './sourceRoleTruthGuard';
import {
  parseNumberedRefTokenMembers,
  referenceForbidsVerifiedMarker,
  RELIABILITY_MARKER_POLICY_VERSION,
  type ReliabilityMarkerCorrectionReceipt,
  type ReliabilityMarkerCorrectionRow,
  type ReliabilityMarkerCorrectionStage,
} from './reliabilityMarkerPolicy';

/**
 * FIX 2 (2026-10-13) — deterministic, label-only correction of
 * `[verified Ref N]` members that cite a recorded estimate, assumption or
 * unqualified source (`referenceForbidsVerifiedMarker`). Such a member is
 * relabelled `inferred`; nothing else in the report changes:
 *
 *  - only reader-visible narrative roots are walked (an explicit allowlist
 *    derived from the roots the audit extractor, the source-role guard and the
 *    source-admission projection read); audit, input and frozen-authority
 *    surfaces are never touched, so a marker there keeps blocking;
 *  - a changed token is rebuilt in place as one token in which every member
 *    that has a label carries it explicitly (`[verified Ref 4, Ref 9]` →
 *    `[inferred Ref 4, verified Ref 9]`); inheritance would otherwise move an
 *    `inferred` label onto a primary source;
 *  - token count, citation numbers and member order never change, and the
 *    label is never `unverified` (that word self-qualifies a sentence for
 *    R-75);
 *  - claim text changes in lock-step with the narrative; a claim whose
 *    corrected text would no longer match the corrected narrative keeps its
 *    original text (and its marker row) instead of becoming a ghost claim.
 *
 * Pure and copy-on-write: when nothing changes the same object is returned.
 */

/** Reader-visible roots beyond the audit extractor and source-role guard
 * lists: rendered containers the source-admission projection also walks. */
const RENDERED_PROSE_ROOTS = ['markdown', 'sectionContent', 'sections', 'editorialNarrative', 'customBriefAnswers',
  'customBriefExecutiveSummary', 'customBriefProjectContext', 'customBriefConclusion'] as const;

/** Audit, input and frozen-authority surfaces, never corrected at any depth
 * (plus every `*AtGen` snapshot). */
export const RELIABILITY_MARKER_CORRECTION_EXCLUDED_KEYS: ReadonlySet<string> = new Set([
  'references', 'qaFindings', 'qualityHold', 'qualityHoldFindings', 'qualityReview', 'qualityWarnings',
  'reviewTrace', 'sanityFindings', 'citationAudit', 'generationTelemetry', 'sedText', 'quarantinedOriginal',
  'decisionAnalysis', 'documents', 'documentRefs', 'metadata', 'generationImpactProjection', 'publicSentimentResearch',
  'testFitAuthorityRef', 'campusResourceAdvisory', 'screeningBasis', 'deterministicScreeningValidation', 'factCheck',
  'demonstrationProvenance', 'demonstrationSedProvenance',
]);

const excludedKey = (key: string): boolean => RELIABILITY_MARKER_CORRECTION_EXCLUDED_KEYS.has(key) || /AtGen$/.test(key);

export const RELIABILITY_MARKER_CORRECTION_ROOTS: ReadonlySet<string> = new Set([
  ...EXTRACT_KNOWN_STRING_SECTIONS,
  ...EXTRACT_KNOWN_OBJECT_SECTIONS,
  ...EXTRACT_KNOWN_ARRAY_SECTIONS,
  ...SOURCE_ROLE_REVIEW_ROOTS,
  ...RENDERED_PROSE_ROOTS,
  'editorialClaims',
].filter(key => !excludedKey(key)));

/** Keys whose array entries are `ReportClaim`s: only `.text` is prose. */
const CLAIM_LIST_KEYS = new Set(['claims', 'editorialClaims']);

export const RELIABILITY_MARKER_RECEIPT_ROW_CAP = 200;
export const RELIABILITY_MARKER_RECEIPT_HISTORY = 4;

const REFERENCE_SOURCE_LIMIT = 150;

/** Receipt identities must never look like a citation (renumbering and the
 * appearance walk read every qaFindings string). */
const neutralizeCitationText = (value: string): string => (/\[[^\]]*Ref/i.test(value)
  ? value.replace(/\[/g, '(').replace(/\]/g, ')') : value);

function referenceIdentity(reference: DataSource): ReliabilityMarkerCorrectionRow['referenceIdentity'] {
  return {
    field: neutralizeCitationText(String(reference.field ?? '')),
    source: neutralizeCitationText(String(reference.source ?? '').slice(0, REFERENCE_SOURCE_LIMIT)),
    retrievedAt: neutralizeCitationText(String(reference.retrievedAt ?? '')),
  };
}

type RowDraft = Omit<ReliabilityMarkerCorrectionRow, 'beforeHash' | 'afterHash'>;

function sentenceContainsUnverifiedUnavailableClaim(text: string, offset: number): boolean {
  const before = text.slice(0, offset);
  const after = text.slice(offset);
  const start = Math.max(before.lastIndexOf('. '), before.lastIndexOf('! '), before.lastIndexOf('? '), before.lastIndexOf('\n'));
  const endCandidates = [after.indexOf('. '), after.indexOf('! '), after.indexOf('? '), after.indexOf('\n')].filter(idx => idx >= 0);
  const end = endCandidates.length > 0 ? offset + Math.min(...endCandidates) + 1 : text.length;
  const sentence = text.slice(start >= 0 ? start + 1 : 0, end);
  return /\bunverified\s+developer[- ]provided\s+context\b/i.test(sentence)
    && /\b(?:unavailable\s+in\s+the\s+SED|as\s+n\/a\s+pending|pending\s+state\s+database\s+verification)\b/i.test(sentence);
}

function correctText(text: string, fieldPath: string, references: readonly DataSource[], rows: ReliabilityMarkerCorrectionRow[]): string {
  if (!text.includes('[')) return text;
  const drafts: RowDraft[] = [];
  let tokenOrdinal = -1;
  const corrected = text.replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags), (token, ...args) => {
    const matchOffset = typeof args.at(-2) === 'number' ? (args.at(-2) as number) : 0;
    tokenOrdinal += 1;
    const members = parseNumberedRefTokenMembers(token);
    // Every member of a matched token parses; anything else stays untouched
    // (and keeps whatever finding it has) rather than losing a citation.
    if (members.length !== token.slice(1, -1).split(',').length) return token;
    const relabel = new Set<number>();
    for (const member of members) {
      if (member.effectiveLabel !== 'verified' || !Number.isSafeInteger(member.n) || member.n < 1) continue;
      const reference = references[member.n - 1];
      const unverifiedSentence = sentenceContainsUnverifiedUnavailableClaim(text, matchOffset);
      if (!reference || (!referenceForbidsVerifiedMarker(reference) && !unverifiedSentence)) continue;
      relabel.add(member.position);
      drafts.push({
        fieldPath, tokenOrdinalInField: tokenOrdinal, memberPosition: member.position,
        referenceOrdinalAtStage: member.n, referenceIdentity: referenceIdentity(reference),
        sourceGrade: reference.sourceGrade ?? null, qualification: reference.qualification ?? null,
        fromLabel: 'verified', toLabel: 'inferred',
      });
    }
    if (!relabel.size) return token;
    return `[${members.map(member => {
      const label = relabel.has(member.position) ? 'inferred' : member.effectiveLabel;
      return `${label ? `${label} ` : ''}Ref ${member.digits}`;
    }).join(', ')}]`;
  });
  if (!drafts.length) return text;
  const beforeHash = sha256Hex(text), afterHash = sha256Hex(corrected);
  for (const draft of drafts) rows.push({ ...draft, beforeHash, afterHash });
  return corrected;
}

function walk(value: unknown, path: string, references: readonly DataSource[], rows: ReliabilityMarkerCorrectionRow[]): unknown {
  if (typeof value === 'string') return correctText(value, path, references, rows);
  if (Array.isArray(value)) {
    let next: unknown[] | undefined;
    value.forEach((item, index) => {
      const corrected = walk(item, `${path}[${index}]`, references, rows);
      if (corrected !== item) (next ??= value.slice())[index] = corrected;
    });
    return next ?? value;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    let next: Record<string, unknown> | undefined;
    for (const [key, child] of Object.entries(record)) {
      if (excludedKey(key)) continue;
      const childPath = path ? `${path}.${key}` : key;
      const corrected = CLAIM_LIST_KEYS.has(key) ? walkClaims(child, childPath, references, rows) : walk(child, childPath, references, rows);
      if (corrected !== child) (next ??= { ...record })[key] = corrected;
    }
    return next ?? value;
  }
  return value;
}

function walkClaims(value: unknown, path: string, references: readonly DataSource[], rows: ReliabilityMarkerCorrectionRow[]): unknown {
  if (!Array.isArray(value)) return value;
  let next: unknown[] | undefined;
  value.forEach((claim, index) => {
    let corrected: unknown = claim;
    if (typeof claim === 'string') corrected = correctText(claim, `${path}[${index}]`, references, rows);
    else if (claim && typeof claim === 'object' && typeof (claim as { text?: unknown }).text === 'string') {
      const text = (claim as { text: string }).text;
      const fixed = correctText(text, `${path}[${index}].text`, references, rows);
      if (fixed !== text) corrected = { ...(claim as Record<string, unknown>), text: fixed };
    }
    if (corrected !== claim) (next ??= value.slice())[index] = corrected;
  });
  return next ?? value;
}

/** Correct a report's reader-visible narrative. Returns the same object and
 * no rows when nothing needs a label change. */
export function correctUnsupportedVerifiedMarkers(report: SiteRagReport): { report: SiteRagReport; rows: ReliabilityMarkerCorrectionRow[] } {
  const references = report.references ?? [];
  if (!references.length) return { report, rows: [] };
  const record = report as unknown as Record<string, unknown>;
  const rows: ReliabilityMarkerCorrectionRow[] = [];
  let next: Record<string, unknown> | undefined;
  for (const root of RELIABILITY_MARKER_CORRECTION_ROOTS) {
    if (!Object.hasOwn(record, root)) continue;
    const child = record[root];
    const corrected = CLAIM_LIST_KEYS.has(root) ? walkClaims(child, root, references, rows) : walk(child, root, references, rows);
    if (corrected !== child) (next ??= { ...record })[root] = corrected;
  }
  if (!next || !rows.length) return { report, rows: [] };
  const out = next as unknown as SiteRagReport;
  return keepMatchingClaims(report, out, rows);
}

/** A claim survives renumbering only while its text still occurs in the
 * narrative. If a claim quoted a copy outside the allowlist, its corrected
 * text would no longer match: keep that claim's original text (fail closed,
 * its marker still blocks) rather than drop the claim as a ghost. */
function keepMatchingClaims(original: SiteRagReport, corrected: SiteRagReport, rows: ReliabilityMarkerCorrectionRow[]) {
  const before = original.claims, after = corrected.claims;
  if (!Array.isArray(before) || !Array.isArray(after) || before === after) return { report: corrected, rows };
  const originalText = collectClaimMatchText(original), correctedText = collectClaimMatchText(corrected);
  const matches = (haystack: string, text: unknown) => {
    const norm = typeof text === 'string' ? normaliseClaimTextForMatch(text) : '';
    return norm.length > 0 && haystack.includes(norm);
  };
  let claims: typeof after | undefined;
  after.forEach((claim, index) => {
    const prior = before[index];
    if (claim === prior || !prior) return;
    if (matches(originalText, prior.text) && !matches(correctedText, claim.text)) (claims ??= after.slice())[index] = prior;
  });
  if (!claims) return { report: corrected, rows };
  const reverted = new Set(claims.flatMap((claim, index) => (claim === before[index] && after[index] !== before[index] ? [`claims[${index}].text`] : [])));
  const kept = rows.filter(row => !reverted.has(row.fieldPath));
  const report = { ...corrected, claims } as SiteRagReport;
  return kept.length ? { report, rows: kept } : { report: original, rows: [] };
}

/** Apply the correction and append its token-free receipt to
 * `qaFindings.reliabilityMarkerCorrections` (last four kept). */
export function applyReliabilityMarkerCorrection(
  report: SiteRagReport,
  options: { stage: ReliabilityMarkerCorrectionStage; sed?: { id?: string | null; version?: number | null } | null },
): SiteRagReport {
  const { report: corrected, rows } = correctUnsupportedVerifiedMarkers(report);
  if (!rows.length) return report;
  const receipt: ReliabilityMarkerCorrectionReceipt = {
    version: RELIABILITY_MARKER_POLICY_VERSION,
    stage: options.stage,
    sedId: options.sed?.id ?? null,
    sedVersion: options.sed?.version ?? null,
    referencesHash: hashCanonicalJson(report.references ?? []),
    rows: rows.slice(0, RELIABILITY_MARKER_RECEIPT_ROW_CAP),
    truncatedRowCount: Math.max(0, rows.length - RELIABILITY_MARKER_RECEIPT_ROW_CAP),
  };
  const prior = corrected.qaFindings?.reliabilityMarkerCorrections ?? [];
  logAuditEvent({
    type: 'reliability-marker-corrected',
    reportId: report.id,
    reportType: report.reportType,
    siteId: report.siteId,
    payload: {
      stage: options.stage,
      policyVersion: RELIABILITY_MARKER_POLICY_VERSION,
      relabelledMembers: rows.length,
      fields: new Set(rows.map(row => row.fieldPath)).size,
      references: new Set(rows.map(row => row.referenceOrdinalAtStage)).size,
    },
  });
  return {
    ...corrected,
    qaFindings: {
      ...corrected.qaFindings,
      reliabilityMarkerCorrections: [...prior, receipt].slice(-RELIABILITY_MARKER_RECEIPT_HISTORY),
    },
  };
}
